import { createSelector } from 'reselect';
import { EMPTY_TEXT, NUMBER_ZERO } from '@mitchell/react.component.ux';
import { IntelligentAssignmentSummary, intelligentPhotoCaptureConstants, PhotoCaptureConstants, PhotoCaptureMessageConstants } from '../constants';
import { IntelligentPhotoCaptureContextUpdateOperation, IntelligentPhotoCaptureEstimatorContentType, IntelligentPhotoCaptureViewType, PhotoCaptureAttributeType } from '../enums';
const getPhotoCaptureSource = (state) => state.domain.photoCapture;
export const getPhotoCaptureEvaluationDocId = (state) => getPhotoCaptureSource(state).evaluationDocId;
export const getPhotoCaptureQuestionnaireId = (state) => getPhotoCaptureSource(state).questionnaireId;
export const getPhotoCaptureWorkItemStatus = (state) => getPhotoCaptureSource(state).workItemStatus;
export const getPhotoCaptureUnderWritingCompanyName = (state) => getPhotoCaptureSource(state).underWritingCompanyName || '';
export const hasPhotoCaptureContextUpdated = (state) => getPhotoCaptureSource(state).isContextUpdated;
export const getPhotoCaptureCompanyCode = (state) => getPhotoCaptureSource(state).companyCode;
export const getPhotoCaptureIsTermsAndConditionAccepted = (state) => getPhotoCaptureSource(state).isTermsAndConditionAccepted;
export const getPhotoCaptureAttachmentInfo = (state) => getPhotoCaptureSource(state).attachmentInfo;
export const getPhotoCaptureContextId = (state) => getPhotoCaptureSource(state).contextId;
export const getPhotoCaptureAssignmentType = (state) => getPhotoCaptureSource(state).assignmentType;
export const getPhotoCaptureAssignmentSubType = (state) => getPhotoCaptureSource(state).assignmentSubType;
export const getPhotoCaptureVideoSizeLimit = (state) => getPhotoCaptureSource(state).videoSizeLimit;
export const getPhotoCaptureClaimNumber = (state) => getPhotoCaptureSource(state).claimNumber;
export const getPhotoCaptureFraudStatement = (state) => getPhotoCaptureSource(state).fraudStatement;
export const getPhotoCaptureFraudWarningAccept = (state) => getPhotoCaptureSource(state).fraudWarningAccept;
export const getPhotoCapturePbeAsgTaskId = (state) => getPhotoCaptureSource(state).pbeAsgTaskId;
export const getPhotoCaptureIsFraudWarningApiStatus = (state) => getPhotoCaptureSource(state).isFraudWarningApiStatus;
export const getPhotoCaptureImageMaxWidth = (state) => parseInt(getPhotoCaptureSource(state).imageMaxWidth || PhotoCaptureConstants.ZERO_STRING);
export const getPhotoCaptureIsQuestionnaireUpdated = (state) => getPhotoCaptureSource(state).isQuestionnaireUpdated || false;
export const getPhotoCaptreIsFileUploadStarted = (state) => getPhotoCaptureSource(state).isFileUploadStarted || false;
export const getPhotoCaptureDisableLibrary = (state) => getShouldUseOnlyCameraOrDisablelibrary(state);
export const getPhotoCaptureFileSizeLimit = (state) => getPhotoCaptureSource(state).fileSizeLimit;
export const getPhotoCaptureAttachedArtifactIds = (state) => getPhotoCaptureSource(state).attachedArtifactIds;
export const getPhotoCaptureCountryCode = (state) => getPhotoCaptureSource(state).countryCode;
export const getPhotoCaptureCategories = (state) => getPhotoCaptureSource(state).categories;
/**
 * Memoized selector to get the estimator summary submit success flag
 * Uses createSelector for performance optimization through memoization
 * @param state - Global photo capture state
 * @returns Boolean indicating if estimator summary submit was successful
 */
export const getIsEstimatorSummarySubmitSuccess = createSelector([getPhotoCaptureSource], (photoCaptureState) => !!photoCaptureState.isEstimatorSummarySubmitSuccess);
export const getIsLinkExpired = (state) => !!getPhotoCaptureSource(state).isLinkExpired;
export const getIsSessionExpired = createSelector([getPhotoCaptureSource], (photoCaptureState) => !!photoCaptureState.isSessionExpired);
export const getShouldShowCommentBox = (state) => {
    const personaConfiguration = getPersonaConfiguration(state);
    // Priority is given to the Persona configuration. If Persona is not available or its key is false,
    return (personaConfiguration && personaConfiguration.shouldShowCommentBox) || false;
};
export const getFoldersOptionList = (state) => getPhotoCaptureSource(state).folderOptionList || [];
export const getMaxFileCount = (state) => getPhotoCaptureSource(state).maxPhotosLimit || 0;
export const getDynamicRedirectURL = (state) => getPhotoCaptureSource(state).dynamicRedirectURL || null;
export const getPersonaConfiguration = (state) => getPhotoCaptureSource(state).personaConfiguration;
export const getShouldAllowMitchellIntelligent = createSelector([getPersonaConfiguration], (personaConfiguration) => {
    // Priority is given to the Persona configuration. If Persona is not available then it will return false,
    if (personaConfiguration) {
        return !!personaConfiguration.shouldAllowMitchellIntelligentPhotoCapture;
    }
    else {
        return false;
    }
});
const getShouldUseOnlyCameraOrDisablelibrary = (state) => {
    const { disableLibrary, personaConfiguration } = getPhotoCaptureSource(state);
    if (disableLibrary === PhotoCaptureConstants.Y || (personaConfiguration && personaConfiguration.shouldUseOnlyCamera))
        return PhotoCaptureConstants.Y;
    else
        return PhotoCaptureConstants.N;
};
export const getMaxChildDamagePanelCount = (state) => getPhotoCaptureSource(state).maxChildDamagePanelCount || NUMBER_ZERO;
export const getCapturedUploadPhotoFiles = (state) => getPhotoCaptureSource(state).files;
export const getIntelligentPhotoOCRInfo = (state) => getPhotoCaptureSource(state).ocrInfo || { ocrText: EMPTY_TEXT, errorMessage: EMPTY_TEXT };
export const getShouldMoveToNextQuestion = (state) => getPhotoCaptureSource(state).shouldMoveToNextQuestion;
export const intelligentGetPhotoCaptureFileUploadView = (state) => getPhotoCaptureSource(state).view || IntelligentPhotoCaptureViewType.NONE;
export const getIntelligentCapturedPhotoFile = (state) => getPhotoCaptureSource(state).capturedPhoto;
export const getIntelligentSequenceScreenName = (state) => getPhotoCaptureSource(state).sequenceScreenName || intelligentPhotoCaptureConstants.SEQUENCE_SCREEN_NAME;
export const getIntelligentPhotoCaptureImageDetail = (state) => getPhotoCaptureSource(state).imageDetailsData;
export const getIntelligentIsPhotoCapureLoading = (state) => !!getPhotoCaptureSource(state).isLoading;
export const getRedirectMethod = (state) => getPhotoCaptureSource(state).redirectionMethod || PhotoCaptureMessageConstants.AUTO;
export const getShouldEnableOCRImageTextDecode = createSelector([getPersonaConfiguration], (personaConfiguration) => {
    return !!personaConfiguration && !!personaConfiguration.shouldEnableOCRImageTextDecode;
});
export const getIntelligentPhotoCaptureserviceFailStatus = (state) => {
    return getPhotoCaptureSource(state).serviceFailStatus;
};
export const getIntelligentPhotoCapturePRGMiscDetails = (state) => {
    return getPhotoCaptureSource(state).prgMiscImage;
};
export const getShouldEnableImageQualityAssessment = createSelector([getPersonaConfiguration], (personaConfiguration) => {
    return !!personaConfiguration && !!personaConfiguration.shouldEnableImageQualityAssessment;
});
export const getIntelligentPhotoCaptureLastRenderedView = (state) => {
    return getPhotoCaptureSource(state).lastRenderedView || IntelligentPhotoCaptureViewType.NONE;
};
export const getIntelligentEstimatorSelectedTileId = (state) => getPhotoCaptureSource(state).estimatorSelectedTileId || 0;
export const getIntelligentEstimatorThumbnailGalleryTemporaryAttachments = (state) => getPhotoCaptureSource(state).estimatorThumbnailGalleryTemporaryAttachments;
export const getIntelligentEstimatorThumbnailGalleryAttachments = createSelector([getIntelligentEstimatorThumbnailGalleryTemporaryAttachments], galleryAttachmentState => (galleryAttachmentState && galleryAttachmentState.galleryAttachments) || []);
export const getIntelligentEstimatorThumbnailGalleryDeletedAttachments = createSelector([getIntelligentEstimatorThumbnailGalleryAttachments], galleryAttachments => galleryAttachments.filter(attachments => attachments.operationType === IntelligentPhotoCaptureContextUpdateOperation.DELETE));
export const getIntelligentEstimatorThumbnailGalleryUploadAttachments = createSelector([getIntelligentEstimatorThumbnailGalleryAttachments], galleryAttachments => galleryAttachments.filter(attachments => attachments.operationType !== IntelligentPhotoCaptureContextUpdateOperation.DELETE));
export const getShouldNewImageUploaded = (state) => getPhotoCaptureSource(state).shouldNewImageUploaded || false;
export const getShouldEnableAdditionalOption = createSelector([getPersonaConfiguration], (personaConfiguration) => {
    return !!personaConfiguration && !!personaConfiguration.shouldEnableAdditionalOption;
});
export const getResourceDetails = (state) => getPhotoCaptureSource(state).resourceDetails || null;
export const getShouldEnableRepairShop = createSelector([getPersonaConfiguration], (personaConfiguration) => {
    return (personaConfiguration === null || personaConfiguration === void 0 ? void 0 : personaConfiguration.shouldEnableRepairShop) || sessionStorage.getItem('isShopSelect') === 'true';
});
export const getShouldAllowVinDecode = createSelector([getPersonaConfiguration], (personaConfiguration) => !!personaConfiguration && !!personaConfiguration.shouldAllowVinDecode);
export const getShopUpdatedValueByUser = (state) => getPhotoCaptureSource(state).isShopUpdatedByUser || false;
export const getDownloadArtifactAuthDetails = (state) => getPhotoCaptureSource(state).downloadArtifactAuthDetailsInfo;
export const getIsShopSelectionScreenVisited = (state) => getPhotoCaptureSource(state).isShopSelectionScreenVisited || false;
export const getIsFolderServiceCompleted = (state) => getPhotoCaptureSource(state).isFolderServiceCompleted || false;
/**
 * Selector to get the current photo capture screen name
 * Returns the screen name from state, or defaults to DEFAULT if not set
 * @param state - Global photo capture state
 * @returns The current screen name
 */
export const getPhotoCaptureScreenName = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.screenName || PhotoCaptureAttributeType.DEFAULT);
/**
 * Selector to get estimated summary files info
 * Returns array of uploaded files in the Summary workflow
 */
export const getEstimatedSummaryFilesInfo = createSelector([getPhotoCaptureSource], (photoCaptureState) => { var _a; return (_a = photoCaptureState.estimatedSummaryFilesInfo) !== null && _a !== void 0 ? _a : []; });
/**
 * Selector to get the maximum number of photos allowed per batch
 * @returns The maximum photo limit per batch (guaranteed number)
 */
export const getIntelligentMaxPhotoLimitForBatch = createSelector([getPhotoCaptureSource], (photoCaptureState) => {
    var _a;
    return (_a = photoCaptureState.maxPhotoLimitForBatch) !== null && _a !== void 0 ? _a : IntelligentAssignmentSummary.MAX_PHOTO_LIMIT_FOR_BATCH;
});
/**
 * Selector to get the maximum number of concurrent batches allowed
 * Falls back to constant if no value in state
 * @param state - Global photo capture state
 * @returns The maximum concurrent batch limit (guaranteed number)
 */
export const getIntelligentMaxConcurrentBatchAllowed = createSelector([getPhotoCaptureSource], (photoCaptureState) => {
    var _a;
    return (_a = photoCaptureState.maxConcurrentBatchAllowed) !== null && _a !== void 0 ? _a : IntelligentAssignmentSummary.MAX_CONCURRENT_BATCH_ALLOWED;
});
/**
 * Selector to get attachment batch activity information
 * Returns array of batch activity info for intelligent photo capture processing
 */
export const getAttachmentBatchActivityInfo = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.attachmentBatchActivityInfo || []);
/**
 * Selector to retrieve temporary attachment information for photo capture assignments.
 * This is used to manage the state of attachments during the upload process before they are finalized.
 */
export const getPhotoCaptureTempAttachmentInfo = (state) => getPhotoCaptureSource(state).tempAttachmentInfo || [];
/**
 * Combined selector that efficiently retrieves all photo capture context data
 * in a single memoized operation. This reduces multiple selector calls and improves
 * performance by batching state selections together.
 */
export const getEstimatorContextState = createSelector([getPhotoCaptureCompanyCode, getAttachmentBatchActivityInfo, getEstimatedSummaryFilesInfo], (companyCode, attachmentBatchActivityInfo, estimatedSummaryFilesInfo) => ({
    companyCode,
    attachmentBatchActivityInfo,
    estimatedSummaryFilesInfo
}));
/**
 * Selector to retrieve all intelligent photo capture attachment iteration info from state.
 */
export const getAttachmentIterationInfo = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.attachmentIterationInfo || []);
/**
 * Selector to retrieve vehicle information for photo capture assignments.
 * This provides details about the vehicle associated with the current assignment,
 * such as year, make, model, VIN, and other identifiers.
 */
export const getPhotoCaptureVehicle = (state) => getPhotoCaptureSource(state).vehicle;
/**
 * Selector to determine if the photo capture has an estimator workflow.
 * Returns a boolean indicating whether the estimator workflow is enabled.
 */
export const getHasEstimatorWorkflow = (state) => !!getPhotoCaptureSource(state).hasEstimatorWorkflow;
/**
 * Selector to retrieve the requester's company code.
 * Returns the company code string or empty text if not available.
 */
export const getRequesterCoCd = (state) => getPhotoCaptureSource(state).requesterCoCd || EMPTY_TEXT;
/**
 * Selector to retrieve the estimator file upload error information.
 * Returns an array of file upload error data related to the estimator workflow.
 */
export const getPhotoCaptureEstimatorFileUploadError = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.estimatorFileUploadError || []);
/**
 * Selector to retrieve the GET damage detection response from photo capture state (memoized).
 * Returns the damage detection response object or undefined/null if not present.
 */
export const getPhotoCaptureDamageDetectionGetData = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.damageDetectionData);
export const getPhotoCaptureSignalRConnectionId = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.signalRConnectionId || EMPTY_TEXT);
export const getIsSignalRConnected = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.isSignalRConnected === true);
export const getIsSignalRConnectionFailed = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.isSignalRConnectionFailed === true);
/**
 * Selector to get instanceReferenceId from state
 * Returns the instanceReferenceId string or empty string if not present
 * @param state - Global photo capture state
 * @returns The instanceReferenceId string
 */
export const getInstanceReferenceId = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.instanceReferenceId ? photoCaptureState.instanceReferenceId : EMPTY_TEXT);
export const getIsVinMatched = (state) => getPhotoCaptureSource(state).isVinMatched || false;
export const getIsLicensePlateMatched = (state) => getPhotoCaptureSource(state).isLicensePlateMatched || false;
export const getIsOdometerMatched = (state) => getPhotoCaptureSource(state).isOdometerMatched || false;
/**
 * Selector to get the isConfidence flag for showing confidence scores
 * @param state - Global photo capture state
 * @returns The isConfidence boolean value
 */
export const getIsConfidence = createSelector([getPhotoCaptureSource], (photoCaptureState) => {
    var _a;
    const storedValue = sessionStorage.getItem('isConfidence');
    return (_a = photoCaptureState.isConfidence) !== null && _a !== void 0 ? _a : (storedValue ? storedValue === 'true' : false);
});
/**
 * Selector to get the PRTC details data from state
 * Returns the PRTC details response containing part codes and localized descriptions
 * @param state - Global photo capture state
 * @returns The PRTC details data or undefined if not yet fetched
 */
export const getPhotoCapturePrtcDetailsData = (state) => getPhotoCaptureSource(state).prtcDetailsData;
/**
 * Selector to get the isPolling flag for photo capture.
 * Returns the value from Redux state (photoCaptureState.usePolling) if present,
 * otherwise falls back to sessionStorage ('isPolling' key).
 * Uses createSelector for memoization, but sessionStorage is checked on every call.
 *
 * @param state - Global photo capture state
 * @returns The isPolling boolean value (true if either Redux or sessionStorage is true)
 */
export const getIsPolling = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.usePolling || sessionStorage.getItem('isPolling') === 'true');
/**
 * Selector to get the list of requestIds that have already had a GET damage detection call dispatched.
 * Used to prevent duplicate GET damage calls for the same requestId when SignalR notifications arrive.
 * @param state - Global photo capture state
 * @returns Array of already-processed requestId strings
 */
export const getProcessedDamageDetectionRequestIds = (state) => getPhotoCaptureSource(state).processedDamageDetectionRequestIds || [];
/* Selector to get the content type for the estimator VIN decode view
 * @param state - Global photo capture state
 * @returns The content type for the estimator VIN decode view
 */
export const getPhotoCaptureContentType = createSelector([getPhotoCaptureSource], (photoCaptureState) => { var _a; return (_a = photoCaptureState.contentType) !== null && _a !== void 0 ? _a : IntelligentPhotoCaptureEstimatorContentType.ODOMETER; });
export const getShouldUseIntegratedCamera = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.shouldUseIntegratedCamera || false);
/**
 * Selector to retrieve user vehicle information for photo capture assignments.
 * @param state - Global photo capture state
 * @returns The user vehicle information or undefined
 */
export const getPhotoCaptureUserVehicleInfo = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.userVehicleInfo);
/**
 * Selector to retrieve the estimator carrier company code from the photo capture context state.
 * Uses createSelector for memoization and performance optimization.
 *
 * @function getPhotoCaptureCarrierCompanyCode
 * @param {IPhotoCaptureGlobalState} state - The global photo capture Redux state.
 * @returns {string} The carrier company code, or an empty string if not present in state.
 */
export const getPhotoCaptureCarrierCompanyCode = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.carrierCompanyCode || EMPTY_TEXT);
/**
 * Selector to retrieve the estimator instance count from the photo capture context state.
 * Uses createSelector for memoization and performance optimization.
 *
 * @function getPhotoCaptureEstimatorInstanceCount
 * @param {IPhotoCaptureGlobalState} state - The global photo capture Redux state.
 * @returns {number} The estimator instance count, or 0 if not present in state.
 */
export const getPhotoCaptureEstimatorInstanceCount = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.estimatorInstanceCount || NUMBER_ZERO);
/**
 * Selector to retrieve the intelligent estimator configuration from the photo capture context state.
 * Uses createSelector for memoization and performance optimization.
 *
 * @function getPhotoCaptureEstimatorConfiguration
 * @param {IPhotoCaptureGlobalState} state - The global photo capture Redux state.
 * @returns {IIntelligentPhotoCaptureEstimatorConfiguration | undefined} The estimator configuration object, or undefined if not present in state.
 */
export const getPhotoCaptureEstimatorConfiguration = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.estimatorConfiguration);
/**
 * Selector to retrieve toast notification details from the photo capture state.
 * Uses createSelector for memoization and returns the current toast configuration if available.
 *
 * @function getPhotoCaptureToastNotification
 * @param {IPhotoCaptureGlobalState} state - The global photo capture Redux state.
 * @returns {IIntelligentPhotoCaptureToastNotification | undefined} The toast notification data, or undefined if none exists.
 */
export const getPhotoCaptureToastNotification = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.toastNotification);
/**
 * Selector to retrieve OCR user verified flags from the photo capture context state.
 * Returns flags indicating whether user has manually verified VIN, license plate or odometer.
 * Uses createSelector for memoization and performance optimization.
 * Called once in image analyze saga - destructure for clean usage.
 *
 * @function getOCRUserVerifiedFlags
 * @param {IPhotoCaptureGlobalState} state - The global photo capture Redux state.
 * @returns {IIntelligentPhotoCaptureOCRUserVerification | undefined} The OCR user verified flags, or undefined if not present in state.
 */
export const getOCRUserVerifiedFlags = createSelector([getPhotoCaptureSource], (photoCaptureState) => photoCaptureState.ocrUserVerification);
//# sourceMappingURL=getPhotoCaptureData.js.map